import React, { useState } from "react";
import PropTypes from "prop-types";
import { Upload } from "@progress/kendo-react-upload";
import ImageCompressionDialog from "../dialogs/ImageCompressionDialog";
import { stringToBytes } from "../utils/convert";
import "./Upload.scss";

const USER_KEY = "User";

/**
 * Upload component
 * @module
 * @param {bool} [disabled] - Is upload readonly?
 * @param {string} url - Url
 * @param {string} name - Name
 * @param {array} [value] - Current value
 * @param {object} [data] - Data
 * @param {func} [onUpload] - Function invoked on file upload
 * @param {number} [tabindex] - Upload tab index
 * @param {func} [onBlur] - Function invoked on blur
 * @param {object} [restrictions] - Upload restriction
 * @param {string} actionsLayout - customize the layout of the action buttons - "start" / " end" / "center" / "stretched"
 */
const UploadComponent = (props) => {
  // const {
  //   url,
  //   autoUpload,
  //   errorText,
  //   showHint,
  //   disabled,
  //   tabindex,
  //   value,
  //   restrictions,
  //   multiple,
  //   listItemUI,
  //   data,
  //   onBlur,
  //   onUpload,
  //   requiredMaximumSize,
  // } = props;

  const [files, setFiles] = useState([]);
  const [isImageCompressionDialogOpen, setImageCompressionDialogOpen] =
    useState(false);

  const handleImageCompressionDialogClose = () => {
    setImageCompressionDialogOpen(false);
  };

  const handleImageCompressionDialogConfirm = () => {
    // Handle image compression dialog confirm action
  };

  const onChange = (files) => {
    props.onChange(props.name, files);
    setFiles(files);

    if (!files.some((file) => file.l_resize)) {
      setImageCompressionDialogOpen(false);
    }
  };

  const onAdd = (event) => {
    onChange(event.newState);

    if (props.autoUpload) {
      return;
    }

    const affectedFiles = event.affectedFiles.map((file) => ({
      ...file,
      l_resize:
        /^\.(jpe?g|png|gif|bmp)$/i.test(file.extension) &&
        props.requiredMaximumSize &&
        file.size > stringToBytes(props.requiredMaximumSize),
    }));

    if (affectedFiles.some((file) => file.l_resize)) {
      setImageCompressionDialogOpen(true);
      setFiles(affectedFiles);
    }
  };

  const onRemove = (event) => {
    // console.log('onRemove: ', event.affectedFiles);
    onChange(event.newState);
  };

  const onProgress = (event) => {
    // console.log('onProgress: ', event.affectedFiles, event.serverResponse);
    onChange(event.newState);
  };

  const onStatusChange = (event) => {
    //console.log('onStatusChange: ', event.affectedFiles, event.serverResponse);
    onChange(event.newState);
    if (props.onUpload && event.response) {
      props.onUpload(event.response.response);
    }
  };

  const handleBeforeSend = (event) => {
    event.additionalData = props.data || {};
    event.headers.Authorization = getAuthorizationHeader();
  };

  const getAuthorizationHeader = () => {
    const user = JSON.parse(sessionStorage.getItem(USER_KEY));

    if (user) {
      const token = user.Token;
      return `Bearer ${token}`;
    }

    return null;
  };

  const handleBlur = () => {
    if (props.onBlur) {
      props.onBlur();
    }
  };

  const {
    url,
    autoUpload,
    errorText,
    showHint,
    disabled,
    tabindex,
    value,
    restrictions,
    multiple,
    listItemUI,
    showActionButtons,
    actionsLayout,
    requiredMaximumSize,
  } = props;

  const saveUrl = `${window.config.url}/${url}`;
  return (
    <div
      className={`upload 
                      ${autoUpload ? "" : "upload-hide-k-action-buttons"} 
                      ${showHint ? "upload-dropzone-hint" : ""}`}
    >
      <Upload
        showActionButtons={showActionButtons}
        autoUpload={autoUpload}
        actionsLayout={actionsLayout}
        restrictions={restrictions}
        batch={false}
        multiple={multiple ? true : false}
        files={value}
        onBeforeUpload={handleBeforeSend}
        onBeforeRemove={handleBeforeSend}
        onAdd={onAdd}
        onRemove={onRemove}
        onProgress={onProgress}
        onStatusChange={onStatusChange}
        withCredentials={false}
        saveUrl={saveUrl}
        disabled={disabled}
        tabindex={tabindex}
        onBlur={handleBlur}
        listItemUI={listItemUI}
      />
      {errorText && (
        <div className="k-widget k-upload k-header">
          <ul className="k-upload-files k-reset">
            <li className="k-file k-file-invalid">
              <div className="k-file-single">
                <span className="k-progress"></span>

                <span className="k-file-invalid-extension-wrapper">
                  <span className="k-file-invalid-icon">!</span>
                  <span className="k-file-state"></span>
                </span>

                <span className="k-file-name-size-wrapper">
                  <span className="k-file-name k-file-name-invalid">
                    {errorText}
                  </span>
                </span>
              </div>
            </li>
          </ul>
        </div>
      )}
      {isImageCompressionDialogOpen && (
        <ImageCompressionDialog
          isOpen={isImageCompressionDialogOpen}
          onClose={handleImageCompressionDialogClose}
          onConfirm={handleImageCompressionDialogConfirm}
          files={files}
          requiredMaximumSize={requiredMaximumSize}
          onChange={onChange}
        />
      )}
    </div>
  );
};

UploadComponent.propTypes = {
  autoUpload: PropTypes.bool,
  disabled: PropTypes.bool,
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.array,
  data: PropTypes.object,
  onUpload: PropTypes.func,
  tabindex: PropTypes.number,
  onBlur: PropTypes.func,
  restrictions: PropTypes.object,
  showHint: PropTypes.bool,
  errorText: PropTypes.string,
  multiple: PropTypes.bool,
  requiredMaximumSize: PropTypes.string,
};

export default UploadComponent;
